import { runInAction } from "mobx";
import { useContext } from "react";

import { useStore } from "@vromo/mobx-react/mobx-react";
import { useToasts } from "@vromo/ui/components/Toasts";
import { PageContext } from "../../stores/local/PageStore";
import { RootStore } from "../../stores/RootStore";
import Bugsnag from "@vromo/analytics/bugsnag";
import { GraphErrorType, TraySessionTokenKEY } from "@vromo/api";
import Tracking from '@vromo/analytics';
import { useIntegrationsPage } from "./useIntegrationsPage";



declare let localStorage: {
    [TraySessionTokenKEY]?: string | null;
};


/** Leverage Integrations Page MOBx state context and common functions
 * @note requires container component to be mobx "observer"
 */
export const useIntegrationsSession = () => {

    const {
        authStore: {
            role: { group },
            token,
            user: { contact }
        }
    } = useStore<RootStore>('rootStore');
    const email = contact?.email ?? "nobody@email.com"
    const { setLoading } = useContext(PageContext)
    const { integrationsApi } = useIntegrationsPage()
    const showToast = useToasts();


    /** retry ONCE if current session was expired ( with a new fresh token). Also adds current group and session to the query */
    const retryWithNewToken = async <T>(fn: () => T) => {
        try {
            await getSession()
            return await fn()
        }
        catch (err) {
            if ((err as GraphErrorType).code === 401) {
                await getSession({ refresh: true })
                setLoading(true)
                return await fn()
            }
            else return Promise.reject(err)
        }
    }

    const setSession = (session?: string) => {
        if (!session) {
            runInAction(() => {
                integrationsApi.session = undefined
                // delete pageSettings[Setting.session]
                delete localStorage[TraySessionTokenKEY]
            })
        }
        else {
            runInAction(() => {
                integrationsApi.session = session
                // pageSettings[Setting.session] = session
                localStorage[TraySessionTokenKEY] = session
            })
        }
    }

    /** get integration session from local storage or fetch a new one and store it. Expires in less than an hour */
    const getSession = async ({ refresh = false } = {}): Promise<void> => {

        if (localStorage[TraySessionTokenKEY] && !refresh) {
            setSession(localStorage[TraySessionTokenKEY])
        }
        else {
            try {
                setLoading(true)
                const { session } = await integrationsApi.sessionToken({
                    vromo_group_id: group!.id!.toString(),
                    login_token: token!
                });
                Tracking.track(`New tray integration session token for group '${group!.id!}' by user '${email}'`);
                if (!session)
                    throw new Error(`No integration session token was retrieved`)

                setSession(session)
            }
            catch (err) {
                Bugsnag.notify(err)
                setSession(undefined)
                showToast(`Could not authenticate with integations service`, "error")
            }
            finally {
                setLoading(false)
            }
        }
    }



    return {
        retryWithNewToken,
        getSession,
        setSession
    }

}